import { 
  Sparkles, Flame, Calendar, Heart, Coffee, Utensils, ShoppingBag, 
  Home, Store, Shirt, Gift, Briefcase, Dog, Car,
  Wrench, Scissors, Dumbbell, Music, Book, Stethoscope, Star
} from 'lucide-react';
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { onAuthStateChanged, signOut, GoogleAuthProvider, signInWithCredential, signInWithPopup } from 'firebase/auth';
import { auth, db, googleProvider } from './services/firebaseConfig';
import { doc, getDoc, collection, getDocs, query, where, addDoc, updateDoc, deleteDoc, writeBatch, setDoc } from 'firebase/firestore';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';


// Componentes estáticos críticos UI
import { Toast } from './components/ui/Toast';
import { RoleSelectorModal } from './components/modals/RoleSelectorModal';

// Helpers
import { isUserAdmin, normalizeText, handleFirestoreError } from './utils/firebaseHelpers';

// Lazy Loading para Vistas
const Landing = lazy(() => import('./pages/Landing'));
const DirectoryView = lazy(() => import('./pages/Directory'));
const Detail = lazy(() => import('./pages/Detail'));
const OwnerDashboard = lazy(() => import('./pages/Dashboard'));
const AdminDashboard = lazy(() => import('./pages/Admin'));
const UserProfile = lazy(() => import('./pages/Profile'));
const Legales = lazy(() => import('./pages/Legales'));
const NotFound = lazy(() => import('./pages/NotFound'));

const ICON_MAP = {
  Store, ShoppingBag, Utensils, Coffee, Heart, Star, Flame, Sparkles, Wrench, Scissors, Dumbbell, Music, Book, Stethoscope, Car, Dog, Home, Briefcase, Shirt, Gift
};
// Loader UI ORIGINAL
const GlobalLoader = () => (
  <div className="min-h-screen bg-[#fafafa] flex flex-col items-center justify-center p-6 text-center font-['Outfit']">
    <div className="w-16 h-16 border-[6px] border-gray-200 border-t-violet-600 rounded-full animate-spin mb-6 shadow-[0_0_15px_rgba(139,92,246,0.3)]"></div>
    <h1 className="text-3xl font-black text-gray-900 tracking-tighter animate-pulse flex items-center gap-2">Tu<span className="text-violet-600">Cuadra.</span></h1>
    <p className="text-gray-500 font-medium text-sm uppercase tracking-widest mt-4">Preparando la ciudad...</p>
  </div>
);

// Constantes estáticas globales
const INITIAL_CITIES = [
  { id: '1', name: 'Mar del Plata', province: 'Buenos Aires', image: 'https://i.postimg.cc/vHPCTSkj/mar-del-plata.png', slug: 'mar-del-plata' },
  { id: '2', name: 'Azul', province: 'Buenos Aires', image: 'https://i.postimg.cc/1Xdch6Y4/1azul.png', slug: 'azul' }
];

const DEFAULT_CATEGORIES = [
  { id: 'Todos', label: 'Todo', Icon: Sparkles },
  { id: 'Ofertas', label: 'Ofertas Hoy', Icon: Flame },
  { id: 'Agenda', label: 'Agenda Cultural', Icon: Calendar },
  { id: 'Favoritos', label: 'Mis Favoritos', Icon: Heart },
  { id: 'Restaurantes & Cafés', label: 'Restaurantes & Cafés', Icon: Coffee },
  { id: 'Comidas al Paso', label: 'Comidas al Paso', Icon: Utensils },
  { id: 'Mercados & Sabores', label: 'Mercados & Sabores', Icon: ShoppingBag },
  { id: 'Hogar & Construcción', label: 'Hogar & Construcción', Icon: Home },
  { id: 'Muebles & Deco', label: 'Muebles & Deco', Icon: Store },
  { id: 'Moda & Accesorios', label: 'Moda & Accesorios', Icon: Shirt },
  { id: 'Diseño & Regalos', label: 'Diseño & Regalos', Icon: Gift },
  { id: 'Salud & Belleza', label: 'Salud & Belleza', Icon: Sparkles },
  { id: 'Profesionales & Oficios', label: 'Profesionales & Oficios', Icon: Briefcase },
  { id: 'Mascotas & Veterinarias', label: 'Mascotas & Veterinarias', Icon: Dog },
  { id: 'Autos & Talleres', label: 'Autos & Talleres', Icon: Car }
];

const MainApp = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null); 
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [businesses, setBusinesses] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [dbCities, setDbCities] = useState([]); 
  const [events, setEvents] = useState([]);
  const [toast, setToast] = useState({ message: '', type: '' });
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(true);

  // --- NUEVO: Control del botón físico "Atrás" en Android ---
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const backButtonListener = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        // Si estamos en la Home "/", cerramos la app
        if (location.pathname === '/') {
          CapacitorApp.exitApp();
        } else {
          // Si estamos en cualquier otra vista, volvemos a la anterior
          navigate(-1);
        }
      });
      return () => {
        backButtonListener.remove();
      };
    }
  }, [location, navigate]);

  // Inicialización forzada para que devuelva el Token correcto
  useEffect(() => {
    GoogleAuth.initialize({
      clientId: '173875085463-0o1c3cupq9cbvtktedus50rhafcdd35p.apps.googleusercontent.com',
      scopes: ['profile', 'email'],
      grantOfflineAccess: false 
    });
  }, []);

  const showToast = (message, type = 'success') => { 
    setToast({ message, type }); 
    setTimeout(() => setToast({ message: '', type: '' }), 3500); 
  };

  const handleUserLogin = async () => { 
    try { 
      let firebaseUser;
      
      // --- LÓGICA HÍBRIDA: CELULAR VS WEB ---
      if (Capacitor.isNativePlatform()) {
        // Modo App Instalada (Android/iOS)
        const res = await GoogleAuth.signIn();
        const idToken = res.authentication?.idToken || res.idToken;
        if (!idToken) throw new Error("Token vacío en celular.");
        const credential = GoogleAuthProvider.credential(idToken);
        const result = await signInWithCredential(auth, credential);
        firebaseUser = result.user;
      } else {
        // Modo Web (Brave, Chrome, Safari, PC)
        const result = await signInWithPopup(auth, googleProvider);
        firebaseUser = result.user;
      }

      const userEmail = firebaseUser.email?.toLowerCase();
      const loggedUser = {
        uid: firebaseUser.uid,
        email: userEmail,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL
      };

      // 1. Seteamos al usuario de inmediato para que la app ya lo reconozca
      setUser(loggedUser);

      // 2. Envolvemos las consultas a la base de datos en un bloque de seguridad
      try {
        const userRef = doc(db, 'users', loggedUser.uid);
        const userSnap = await getDoc(userRef);

        let isOwner = false;
        try {
          const bizSnap = await getDocs(query(collection(db, "businesses"), where("ownerEmail", "==", userEmail)));
          isOwner = !bizSnap.empty;
        } catch (e) {
          console.log("Aviso: Lectura de negocios restringida para este usuario.");
        }
        
        if (isUserAdmin(loggedUser)) {
          await setDoc(userRef, { role: 'admin' }, { merge: true });
          setUserData({ role: 'admin' });
          showToast(`¡Bienvenido CEO!`, "success");
        } else if (isOwner) {
          await setDoc(userRef, { role: 'owner' }, { merge: true });
          setUserData({ role: 'owner' });
          showToast(`¡Hola! Acceso a negocio habilitado.`, "success");
        } else if (!userSnap.exists()) { 
          setShowRoleSelector(true); 
        } else { 
          setUserData(userSnap.data()); 
          showToast(`¡Hola de vuelta!`, "success"); 
        }
      } catch (dbError) {
        showToast(`¡Hola! Sesión iniciada.`, "success");
      }
      
    } catch (error) { 
      console.error("Error en login de Google:", error);
      const errorMsg = error?.message || "Error desconocido";
      showToast(`Error: ${errorMsg}`, "error"); 
    } 
  };

  const saveNewUserRole = async (role) => {
    if(!user) return;
    try {
        await setDoc(doc(db, 'users', user.uid), { role }, { merge: true });
        setUserData(prev => ({ ...prev, role })); 
        setShowRoleSelector(false); 
        showToast(`Perfil actualizado`, "success");
    } catch (error) { handleFirestoreError(error, showToast); }
  };

  const handleUserLogout = async () => { 
    try {
        // Solo cerramos sesión en Capacitor si estamos en el celular
        if (Capacitor.isNativePlatform()) {
          try { await GoogleAuth.signOut(); } catch (e) { console.log(e); }
        }
        
        await signOut(auth); 
        setUserData(null); 
        setFavorites(new Set()); 
        setShowRoleSelector(false);
        showToast("Sesión cerrada", "success"); 
    } catch (error) { handleFirestoreError(error, showToast); }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
            const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
            if (userSnap.exists()) { 
              const data = userSnap.data();
              setUserData(data); 
              if (data.favorites) setFavorites(new Set(data.favorites)); 
            }
        } catch (error) { console.error(error); }
      }
      setIsAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const qBiz = query(collection(db, "businesses"), where("status", "==", "approved"));
        const snapBiz = await getDocs(qBiz);
        setBusinesses(snapBiz.docs.map(d => ({ ...d.data(), id: d.id })));
        
        const qCats = await getDocs(collection(db, "categories"));
        setCustomCategories(qCats.docs.map(d => ({ ...d.data(), id: d.id })));
        
        const qCities = await getDocs(collection(db, "cities"));
        setDbCities(qCities.docs.map(d => ({ ...d.data(), id: d.id, dbId: d.id })));
        
        const qEv = await getDocs(collection(db, "events"));
        const now = new Date();
        const validEvents = [];
        qEv.docs.forEach(d => {
          const evData = d.data();
          const eventDateTime = new Date(`${evData.date}T${evData.time}`);
          if (eventDateTime < now) { deleteDoc(doc(db, "events", d.id)).catch(e => console.error(e)); }
          else { validEvents.push({ ...evData, id: d.id }); }
        });
        setEvents(validEvents);
      } catch (error) { console.error(error); }
      setIsDataLoading(false);
    };
    fetchData(); 
  }, []);

  useEffect(() => {
    if (!user?.email) return;
    const fetchUserBiz = async () => {
      try {
        const q = query(collection(db, "businesses"), where("ownerEmail", "==", user.email.toLowerCase()));
        const snap = await getDocs(q);
        const myBiz = snap.docs.map(d => ({ ...d.data(), id: d.id }));
        
        setBusinesses(prev => {
          const combined = [...prev];
          myBiz.forEach(b => {
            if (!combined.find(x => x.id === b.id)) combined.push(b);
          });
          return combined;
        });
      } catch (e) { console.error(e); }
    };
    fetchUserBiz();
  }, [user]);

  const handleAddBusiness = async (newBiz) => {
    try { 
        const bizWithDate = { ...newBiz, createdAt: new Date().toISOString() };
        const docRef = await addDoc(collection(db, "businesses"), bizWithDate); 
        setBusinesses(prev => [...prev, { ...bizWithDate, id: docRef.id }]); 
        showToast("Local agregado con éxito", "success");
    } catch (error) { handleFirestoreError(error, showToast); }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try { 
        await updateDoc(doc(db, "businesses", id), { status: newStatus }); 
        setBusinesses(prev => prev.map(b => b.id === id ? { ...b, status: newStatus } : b)); 
    } catch (error) { handleFirestoreError(error, showToast); }
  };

  const handleEditBusiness = async (id, updatedData) => {
    try {
      const cleanData = Object.fromEntries(Object.entries(updatedData).filter(([_, v]) => v !== undefined));
      await updateDoc(doc(db, "businesses", id), cleanData);
      setBusinesses(prev => prev.map(b => b.id === id ? { ...b, ...cleanData } : b));
      showToast("Cambios aplicados", "success");
    } catch (error) { showToast("Error al guardar", "error"); }
  };

  const handleDeleteBusiness = async (id) => {
    if(!window.confirm('¿Borrar local?')) return;
    try {
      await deleteDoc(doc(db, "businesses", id));
      setBusinesses(prev => prev.filter(b => b.id !== id));
      showToast("Local eliminado", "success");
    } catch(e) { handleFirestoreError(e, showToast); }
  };

  const handleAddCategory = async (catName, iconName = 'Store') => {
    try {
      const newCat = { label: catName, icon: iconName };
      const docRef = await addDoc(collection(db, "categories"), newCat);
      setCustomCategories(prev => [...prev, { ...newCat, id: docRef.id }]);
      showToast("Categoría creada", "success");
    } catch(error) { handleFirestoreError(error, showToast); }
  };

  const handleDeleteCategory = async (id) => {
    if(!window.confirm('¿Borrar categoría?')) return;
    try {
      await deleteDoc(doc(db, "categories", id));
      setCustomCategories(prev => prev.filter(c => c.id !== id));
      showToast("Categoría eliminada", "success");
    } catch(error) { handleFirestoreError(error, showToast); }
  };

  const handleAddCity = async (cityName, imageUrl) => {
    try {
      const newCity = { name: cityName, image: imageUrl, slug: normalizeText(cityName).replace(/ /g, '-'), province: 'Argentina' };
      const docRef = await addDoc(collection(db, "cities"), newCity);
      setDbCities(prev => [...prev, { ...newCity, id: docRef.id, dbId: docRef.id }]);
      showToast("Ciudad creada", "success");
    } catch(error) { handleFirestoreError(error, showToast); }
  };

  const handleDeleteCity = async (id) => {
    if(!window.confirm('¿Borrar ciudad?')) return;
    try {
      await deleteDoc(doc(db, "cities", id));
      setDbCities(prev => prev.filter(c => c.dbId !== id));
      showToast("Ciudad eliminada", "success");
    } catch(error) { handleFirestoreError(error, showToast); }
  };

  const toggleFav = async (id) => {
    if (!user) return handleUserLogin(); 
    const next = new Set(favorites); next.has(id) ? next.delete(id) : next.add(id);
    setFavorites(next); 
    try {
        await setDoc(doc(db, 'users', user.uid), { favorites: Array.from(next) }, { merge: true });
    } catch (error) { handleFirestoreError(error, showToast); }
  };

  const handleDeleteReviewAdmin = async (bizId, reviewId) => {
    try {
      await deleteDoc(doc(db, `businesses/${bizId}/reviews/${reviewId}`));

      const reviewsSnap = await getDocs(collection(db, `businesses/${bizId}/reviews`));
      
      const totalReviews = reviewsSnap.size;
      let newAvgRating = 0;

      if (totalReviews > 0) {
        let sum = 0;
        reviewsSnap.forEach(d => {
          sum += d.data().rating || 0; 
        });
        newAvgRating = sum / totalReviews;
      }

      await updateDoc(doc(db, "businesses", bizId), { 
        avgRating: newAvgRating,
        reviewCount: totalReviews
      });

      setBusinesses(prev => prev.map(b => 
        b.id === bizId ? { ...b, avgRating: newAvgRating, reviewCount: totalReviews } : b
      ));

      showToast("Reseña eliminada y promedio actualizado", "success");
    } catch(e) { handleFirestoreError(e, showToast); }
  };

  const handleAddEvent = async (newEv) => {
    try {
      const ref = await addDoc(collection(db, "events"), newEv);
      setEvents(e => [...e, { ...newEv, id: ref.id }]);
      showToast("Evento publicado", "success");
    } catch (error) { handleFirestoreError(error, showToast); }
  };

  const handleDeleteEvent = async (id) => {
    if(!window.confirm('¿Borrar evento?')) return;
    try {
      await deleteDoc(doc(db, "events", id));
      setEvents(e => e.filter(x => x.id !== id));
      showToast("Evento eliminado", "success");
    } catch (error) { handleFirestoreError(error, showToast); }
  };

  const handleEditEvent = async (id, updatedData) => {
    try {
      await updateDoc(doc(db, "events", id), updatedData);
      setEvents(prev => prev.map(e => e.id === id ? { ...e, ...updatedData } : e));
      showToast("Evento actualizado", "success");
    } catch (error) { handleFirestoreError(error, showToast); }
  };

  const allCategories = [
    ...DEFAULT_CATEGORIES,
    ...customCategories.map(c => ({ 
      id: c.label || c.name || 'Sin Nombre', 
      label: c.label || c.name || 'Sin Nombre', 
      isCustom: true, 
      dbId: c.id,
      Icon: c.icon && ICON_MAP[c.icon] ? ICON_MAP[c.icon] : Store 
    }))
  ];
  const allCities = [...INITIAL_CITIES, ...dbCities];

  if (isAuthLoading || isDataLoading) return <GlobalLoader />;

  return (
    <>
      <div className="relative z-[9999]">
        <Toast {...toast} />
      </div>
      {showRoleSelector && user && !isUserAdmin(user) && <RoleSelectorModal user={user} onSelectRole={saveNewUserRole} showToast={showToast} onCancel={() => userData?.role ? setShowRoleSelector(false) : handleUserLogout()} />}
      <Suspense fallback={<GlobalLoader />}>
        <Routes>
          <Route path="/" element={<Landing cities={allCities} />} />
          <Route path="/ciudad/:slug" element={<DirectoryView businesses={businesses} events={events} categories={allCategories} cities={allCities} favorites={favorites} toggleFav={toggleFav} handleUserLogin={handleUserLogin} handleUserLogout={handleUserLogout} user={user} userData={userData} />} />
          <Route path="/local/:id" element={<Detail businesses={businesses} cities={allCities} showToast={showToast} user={user} />} />
          <Route path="/mi-negocio" element={<OwnerDashboard businesses={businesses} categories={allCategories} cities={allCities} onAddBusiness={handleAddBusiness} onEditBusiness={handleEditBusiness} showToast={showToast} userUid={user?.uid} userEmail={user?.email} userRole={userData?.role} />} />
          <Route path="/mi-perfil" element={<UserProfile user={user} businesses={businesses} favorites={favorites} onChangeRole={() => setShowRoleSelector(true)} />} />
          <Route path="/admin" element={<AdminDashboard businesses={businesses} events={events} categories={allCategories} cities={allCities} onUpdateStatus={handleUpdateStatus} onEditBusiness={handleEditBusiness} onAddBusiness={handleAddBusiness} onAddCategory={handleAddCategory} onDeleteCategory={handleDeleteCategory} onAddCity={handleAddCity} onDeleteCity={handleDeleteCity} onDeleteReview={handleDeleteReviewAdmin} onDeleteBusiness={handleDeleteBusiness} onAddEvent={handleAddEvent} onDeleteEvent={handleDeleteEvent} onEditEvent={handleEditEvent} showToast={showToast} user={user} />} />
          <Route path="/legales" element={<Legales />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default function App() {
  useEffect(() => {
    const link = document.createElement('link'); 
    link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;700;900&display=swap'; 
    link.rel = 'stylesheet'; 
    document.head.appendChild(link);
  }, []);
  return (
    <HelmetProvider>
      <Router>
        <MainApp />
      </Router>
    </HelmetProvider>
  );
}